/**
 * Scene 8: The Closing
 * "Explore the Data"
 */

import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { NigeriaMap } from '../NigeriaMap';
import { ArrowRightIcon, MapPinIcon, ChartLineIcon } from '../../icons/StoryIcons';
import type { StateGeoFeature } from '../../../types';
import type { StoryInsights } from '../../../hooks/useStoryData';

gsap.registerPlugin(ScrollTrigger);

interface Scene8Props {
  insights: StoryInsights;
  features: StateGeoFeature[];
  isActive: boolean;
  onExploreClick?: () => void;
}

export function Scene8Closing({ insights, features, isActive, onExploreClick }: Scene8Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<SVGSVGElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const ctx = gsap.context(() => {
      // Only set initial hidden state if not active
      if (!isActive) {
        gsap.set(contentRef.current, { opacity: 0, y: 50 });
        gsap.set(mapRef.current, { opacity: 0, scale: 0.5, rotation: -10 });
        
        if (statsRef.current) {
          const cards = statsRef.current.querySelectorAll('.stat-card');
          gsap.set(cards, { opacity: 0, y: 30 });
        }
        
        gsap.set(ctaRef.current, { opacity: 0, scale: 0.9 });
      }
      
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top center',
          end: 'center center',
          toggleActions: 'play none none reverse',
        },
      });
      
      tl.to(contentRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      })
      .to(mapRef.current, {
        opacity: 1,
        scale: 1,
        rotation: 0,
        duration: 1.2,
        ease: 'elastic.out(1, 0.8)',
      }, '-=0.4');
      
      if (statsRef.current) {
        const cards = statsRef.current.querySelectorAll('.stat-card');
        tl.to(cards, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out',
        }, '-=0.6');
      }
      
      tl.to(ctaRef.current, {
        opacity: 1,
        scale: 1,
        duration: 0.8,
        ease: 'back.out(1.7)',
      }, '-=0.3');
      
      // Pulsing CTA animation
      gsap.to(ctaRef.current, {
        boxShadow: '0 0 40px rgba(6, 182, 212, 0.4)',
        duration: 1.5,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    }, containerRef);
    
    return () => ctx.revert();
  }, []);
  
  // Force visibility when scene becomes active (for auto-scroll)
  useEffect(() => {
    if (isActive) {
      gsap.to(contentRef.current, { opacity: 1, y: 0, duration: 0.5 });
      gsap.to(mapRef.current, { opacity: 1, scale: 1, rotation: 0, duration: 0.5 });
      gsap.to(ctaRef.current, { opacity: 1, scale: 1, duration: 0.5 });
      
      if (statsRef.current) {
        const cards = statsRef.current.querySelectorAll('.stat-card');
        gsap.to(cards, { opacity: 1, y: 0, duration: 0.5, stagger: 0.05 });
      }
    }
  }, [isActive]);
  
  return (
    <section
      ref={containerRef}
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-4 py-20"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-accent-500/10 via-transparent to-transparent pointer-events-none" />
      
      {/* Radial glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent-500/10 rounded-full blur-3xl" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-4xl w-full">
        <div ref={contentRef} className="text-center mb-12 group">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-white mb-4 transition-all duration-300 group-hover:tracking-wide">
            Ready to Explore?
          </h2>
          <p className="text-xl md:text-2xl text-surface-300 transition-colors duration-300 group-hover:text-surface-200">
            Dive deeper into Nigeria's <span className="transition-all duration-300 hover:text-accent-400 cursor-default">FAAC allocations</span>
          </p>
        </div>
        
        {/* Map */}
        <div className="flex justify-center mb-12">
          <div className="relative">
            <div className="absolute inset-0 bg-accent-500/20 rounded-full blur-3xl animate-pulse" />
            <NigeriaMap
              ref={mapRef}
              features={features}
              width={300}
              height={300}
              defaultColor="#06b6d4"
              animate={isActive}
              className="relative z-10"
            />
          </div>
        </div>
        
        {/* Key stats summary */}
        <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="stat-card group p-4 bg-surface-800/50 backdrop-blur-sm rounded-xl border border-surface-700 text-center cursor-pointer transition-all duration-300 hover:bg-surface-800/70 hover:border-accent-500/30 hover:shadow-lg hover:shadow-accent-500/10 hover:-translate-y-1 hover:scale-105">
            <div className="text-2xl md:text-3xl font-display font-bold text-accent-400 transition-all duration-300 group-hover:text-accent-300 group-hover:scale-110">
              ₦{(insights.totalAllocation / 1e12).toFixed(0)}T+
            </div>
            <div className="text-xs text-surface-400 mt-1 transition-colors duration-300 group-hover:text-surface-300">Total Allocated</div>
          </div>
          
          <div className="stat-card group p-4 bg-surface-800/50 backdrop-blur-sm rounded-xl border border-surface-700 text-center cursor-pointer transition-all duration-300 hover:bg-surface-800/70 hover:border-accent-500/30 hover:shadow-lg hover:shadow-accent-500/10 hover:-translate-y-1 hover:scale-105">
            <div className="text-2xl md:text-3xl font-display font-bold text-white transition-all duration-300 group-hover:text-accent-400 group-hover:scale-110">
              {insights.totalStates}
            </div>
            <div className="text-xs text-surface-400 mt-1 transition-colors duration-300 group-hover:text-surface-300">States + FCT</div>
          </div>
          
          <div className="stat-card group p-4 bg-surface-800/50 backdrop-blur-sm rounded-xl border border-surface-700 text-center cursor-pointer transition-all duration-300 hover:bg-surface-800/70 hover:border-accent-500/30 hover:shadow-lg hover:shadow-accent-500/10 hover:-translate-y-1 hover:scale-105">
            <div className="text-2xl md:text-3xl font-display font-bold text-white transition-all duration-300 group-hover:text-accent-400 group-hover:scale-110">
              {insights.yearsSpan.end - insights.yearsSpan.start + 1}
            </div>
            <div className="text-xs text-surface-400 mt-1 transition-colors duration-300 group-hover:text-surface-300">Years of Data</div>
          </div>
          
          <div className="stat-card group p-4 bg-surface-800/50 backdrop-blur-sm rounded-xl border border-surface-700 text-center cursor-pointer transition-all duration-300 hover:bg-surface-800/70 hover:border-accent-500/30 hover:shadow-lg hover:shadow-accent-500/10 hover:-translate-y-1 hover:scale-105">
            <div className="text-2xl md:text-3xl font-display font-bold text-white transition-all duration-300 group-hover:text-accent-400 group-hover:scale-110">
              {insights.yearlyTotals.length * 12}
            </div>
            <div className="text-xs text-surface-400 mt-1 transition-colors duration-300 group-hover:text-surface-300">Monthly Records</div>
          </div>
        </div>
        
        {/* CTA Buttons */}
        <div ref={ctaRef} className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={onExploreClick}
            className="group flex items-center gap-3 px-8 py-4 bg-accent-500 text-white rounded-xl font-semibold text-lg transition-all duration-300 shadow-glow hover:bg-accent-400 hover:scale-105 hover:-translate-y-1 hover:shadow-xl hover:shadow-accent-500/30"
          >
            <MapPinIcon size={24} className="transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
            Explore Interactive Map
            <ArrowRightIcon 
              size={20} 
              className="transition-transform duration-300 group-hover:translate-x-2" 
            />
          </button>
          
          <a
            href="#"
            className="group flex items-center gap-3 px-8 py-4 bg-surface-800 text-surface-200 rounded-xl font-semibold text-lg border border-surface-600 transition-all duration-300 hover:bg-surface-700 hover:border-surface-500 hover:text-white hover:scale-105 hover:-translate-y-1 hover:shadow-lg"
          >
            <ChartLineIcon size={24} className="transition-transform duration-300 group-hover:scale-110" />
            View Data Sources
          </a>
        </div>
        
        {/* Footer note */}
        <p className="mt-12 text-center text-surface-500 text-sm">
          Data sourced from the Federation Account Allocation Committee (FAAC) • {insights.yearsSpan.start}–{insights.yearsSpan.end}
        </p>
      </div>
    </section>
  );
}

export default Scene8Closing;

